import React, { useState, useEffect, useRef } from 'react';
import styles from '../../components/MobileSearch/MobileSearch.module.scss';
import { useNavigate } from 'react-router';
import { useSearch } from '../../hooks/useSearch';
import Product from '@models/product';
import { generateProductViewUrl } from '@utils/urlUtils';
import { formatDiscountPercentage } from '@utils/formatDiscountPercentage';
import { cartStore, authStore } from '@stores/main';
import { pageTrackingService } from '../../services/pageTracking';
import { useBreadcrumbNavigation } from '../../utils/breadcrumbNavigation';

const SearchMobilePage: React.FC = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const initialLoadDone = useRef(false);
  const { goToLogin } = useBreadcrumbNavigation();
  const { 
    searchQuery, 
    setSearchQuery, 
    searchResults, 
    loading, 
    error,
    triggerEmptySearch 
  } = useSearch();

  useEffect(() => {
    if (!initialLoadDone.current) {
      setSearchQuery('');
      initialLoadDone.current = true;
    }
  }, [setSearchQuery]);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleViewMore = () => {
    navigate(`/search?q=${encodeURIComponent(searchQuery || '')}`);
  };

  const handleAddToCart = async (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }
      pageTrackingService.trackProductAddToCart(product.id, product.title);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const renderSearchResults = () => {
    if (loading) {
      return <div className={styles.loading}>Loading results...</div>;
    }
    if (error) {
      return <div className={styles.error}>{error}</div>;
    }
    if (!searchQuery.trim() && searchResults.length === 0) {
      return (
        <div className={styles.sectionTitle}>BEST DISCOUNT ON GAMES</div>
      );
    }
    if (searchResults.length === 0) {
      return <div className={styles.noResults}>No results found</div>;
    }
    const displayResults = isExpanded ? searchResults : searchResults.slice(0, 6);
    return (
      <>
        <div className={styles.sectionTitle}>
          {searchQuery.trim() && (
            <>
              <div className={styles.searchHeader}>
                You searched for: <span className={styles.searchQueryGradient}>&quot;{searchQuery}&quot;</span>
              </div>
              <div style={{ color: '#b0b0b0', fontSize: 15, marginBottom: 10 }}>
                {searchResults.length} {searchResults.length === 1 ? 'game' : 'games'} found
              </div>
            </>
          )}
          {!searchQuery.trim() && 'BEST DISCOUNTS ON ONEPLAY STORE'}
        </div>
        <div className={`${styles.mobileGameList} ${isExpanded ? styles.scrollableList : ""}`}>
          {displayResults.map((product: Product) => (
            <div 
              key={product.id} 
              className={styles.gameItem}
              onClick={() => {
                navigate(generateProductViewUrl(product));
              }}
            >
              <div className={styles["game-image-container"]}>
                {!!product.discountPercentage && (
                  <>
                    <div className={`${styles["discount-tag"]} ${product.discountPercentage > 99 ? styles["discount-tag-large"] : ''}`}>
                      <span className={`${product.hotOffer ? styles["discount-hotoffer"] : styles["discount-offered"]}` + " is-flex is-align-items-start is-flex-direction-column fs-20"}>
                        {formatDiscountPercentage(product.discountPercentage)}%
                        <span className={styles["discount-off"] + " fs-14 fw-700"}>OFF</span>
                      </span>
                      {product.hotOffer && (
                        <div className={styles["limited-offer-tag"] + " position-absolute"}>
                          <img
                            src={
                              process.env.PUBLIC_URL +
                              "/images/tags/limited-offer-tag.svg"
                            }
                            alt="Limited Offer"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                {product.isSoldOut && (
                  <div className={styles["out-of-stock-tag"] + " position-absolute"}>
                    <span className={styles["tagOutOfStock"]}>Out of Stock</span>
                  </div>
                )}
                <img 
                  src={product.poster || `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`} 
                  alt={product.title}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.onerror = null;
                    target.src = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;
                  }}
                />
              </div>
              <div className={styles.info}>
                <div className={styles.title}>{product.title}</div>
                <div className="is-flex g-5 is-align-items-center">
                  <div className={styles["game-price"] + " fs-16 fw-500 color-lightgray"}>{product.buyPriceString}</div>
                  {product.displayPrice > product.buyPrice && (
                    <div className={styles["strike-price"] + " fs-12 fw-500 f-gray"}>
                      {product.displayPriceString}
                    </div>
                  )}
                </div>
                <div className={styles.gameTags}>
                  <span className={styles.tag}>{product.availableRegion}</span>
                </div>
                <div className={styles["add-to-cart-container"]}>
                  {!cartStore.items.some((item) => item.id === product.id) ? (
                    <div 
                      className={styles["add-to-cart-button"]}
                      onClick={(e) => handleAddToCart(product, e)}
                    >
                      <img
                        src="/images/icons/add-to-cart.svg"
                        alt="Add to Cart"
                        className={styles["add-to-cart-icon"]}
                      />
                    </div>
                  ) : (
                    <div className={styles["added-to-cart-button"]}>
                      <img
                        src="/images/icons/added-to-cart.svg"
                        alt="Added to Cart"
                        className={styles["add-to-cart-icon"]}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        {searchResults.length > 5 && (
          <div className={styles.viewMore}>
            <button 
              className={styles.viewMoreButton}
              onClick={handleViewMore}
            >
              {isExpanded ? "View all games" : "View More"}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles["mobile-search-popup"]}>
      <div className={styles["mobile-search-header"]}>
        <div className={styles["mobile-search-input-container"]} style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search for games or gift cards"
            className={styles["mobile-search-input"]}
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearch}
          />
          <img
            src={`${process.env.PUBLIC_URL}/images/icons/search-icon.svg`}
            alt="Search"
            className={styles["mobile-search-icon"]}
          />
          {/* <button 
            className={styles["mobile-search-close"]}
            onClick={() => navigate(-1)}
            style={{ marginLeft: 8, height: 40, minWidth: 40, background: 'none', border: 'none', color: '#fff', fontSize: 16 }}
          >
            Back
          </button> */}
        </div>
      </div>
      <div className={styles["mobile-search-content"]}>
        {renderSearchResults()}
      </div>
    </div>
  );
};

export default SearchMobilePage; 